import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useParams } from "@tanstack/react-router";
import { useState, type ReactElement } from "react";
import { ConfirmDialog, ContentHeader, Field, Modal, MoneyInput, QueryStatus, useToast } from "@cx/ui";
import type {
  AccountStatement,
  BankTransactionView,
  FinancialAccountView,
  FinancialCategoryView,
} from "@cx/contracts";
import { formatBRL, formatDateBR, todayInSaoPaulo } from "@cx/domain";
import { can, type Permissions } from "@cx/db";
import { ApiFailure } from "../../api";
import { errorMessage } from "../../errors";
import { useSession } from "../../session";
import { ACCOUNT_KIND_LABEL, txLabel } from "./labels";
import { ReconcilePanel } from "../bank/ReconcilePanel";

const money = (c: number | null | undefined) => (c === null || c === undefined ? "—" : formatBRL(c));

/** Extrato da conta (RF-FIN-02): resumo do período, lançamentos manuais, estorno e conciliação do OFX (T2.10). */
export function AccountPage(): ReactElement {
  const { id } = useParams({ from: "/financas/caixas-e-bancos/$id" });
  const { companyId, me, api } = useSession();
  const qc = useQueryClient();
  const toast = useToast();
  const perms = me.permissions as Permissions;
  const canWrite = can(perms, "financial_accounts", "create");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [entry, setEntry] = useState(false);
  const [reversing, setReversing] = useState<BankTransactionView | null>(null);
  const account = useQuery({
    queryKey: ["fin-accounts", companyId, id],
    queryFn: () => api<FinancialAccountView>(`/financial-accounts/${id}`),
  });
  const statement = useInfiniteQuery({
    queryKey: ["fin-accounts", companyId, id, "statement", from, to],
    initialPageParam: "",
    queryFn: ({ pageParam }) =>
      api<AccountStatement>(
        `/financial-accounts/${id}/transactions?${new URLSearchParams({
          ...(from ? { from } : {}),
          ...(to ? { to } : {}),
          ...(pageParam ? { cursor: pageParam } : {}),
        })}`,
      ),
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });
  const rows = statement.data?.pages.flatMap((p) => p.data) ?? [];
  const period = statement.data?.pages[0]?.period;
  const changed = () => qc.invalidateQueries({ queryKey: ["fin-accounts", companyId] });
  const reverse = useMutation({
    mutationFn: (txId: string) =>
      api(`/financial-accounts/transactions/${txId}/reverse`, {
        method: "POST",
        headers: { "Idempotency-Key": crypto.randomUUID() },
        body: JSON.stringify({}),
      }),
    onSuccess: () => {
      toast("success", "Lançamento estornado");
      changed();
    },
    onError: (e) => toast("danger", errorMessage(e)),
  });
  const reversible = (t: BankTransactionView) => !t.reversedAt && !t.reversalOfId && t.kind !== "settlement";

  return (
    <>
      <ContentHeader
        title={account.data?.name ?? "Conta"}
        breadcrumb={["Finanças", "Caixas e bancos", account.data?.name ?? ""]}
        actions={
          canWrite &&
          account.data?.active && (
            <button type="button" className="btn btn-primary" onClick={() => setEntry(true)}>
              Incluir lançamento
            </button>
          )
        }
      />
      <QueryStatus loading={account.isLoading} error={account.error} />
      {account.data && (
        <div className="row g-3 mb-3">
          <div className="col-md-3">
            <div className="box card p-3 h-100">
              <span className="text-muted small">{ACCOUNT_KIND_LABEL[account.data.kind]}</span>
              <span className="fs-5 fw-semibold" data-testid="account-balance">
                {money(account.data.balanceCents)}
              </span>
              <span className="text-muted small">Saldo atual</span>
            </div>
          </div>
          {period && (
            <>
              <div className="col-6 col-md-3">
                <div className="box card p-3 h-100">
                  <span className="text-muted small">Saldo anterior</span>
                  <span className="fw-semibold">{money(period.openingCents)}</span>
                </div>
              </div>
              <div className="col-6 col-md-3">
                <div className="box card p-3 h-100">
                  <span className="text-muted small">Entradas / saídas</span>
                  <span className="fw-semibold text-success">{money(period.inCents)}</span>
                  <span className="fw-semibold text-danger">{money(period.outCents)}</span>
                </div>
              </div>
              <div className="col-md-3">
                <div className="box card p-3 h-100">
                  <span className="text-muted small">Saldo final do período</span>
                  <span className="fw-semibold">{money(period.closingCents)}</span>
                </div>
              </div>
            </>
          )}
        </div>
      )}
      <div className="box card">
        <div className="row g-2 p-3 pb-0">
          <div className="col-6 col-md-2">
            <label className="form-label small" htmlFor="st-from">
              De
            </label>
            <input
              id="st-from"
              type="date"
              className="form-control"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
            />
          </div>
          <div className="col-6 col-md-2">
            <label className="form-label small" htmlFor="st-to">
              Até
            </label>
            <input
              id="st-to"
              type="date"
              className="form-control"
              value={to}
              onChange={(e) => setTo(e.target.value)}
            />
          </div>
        </div>
        <QueryStatus loading={statement.isLoading} error={statement.error} />
        {statement.isSuccess && rows.length === 0 && (
          <p className="text-muted p-3 mb-0">Nenhum lançamento no período.</p>
        )}
        {rows.length > 0 && (
          <div className="table-responsive mt-2">
            <table className="table align-middle mb-0" aria-label="Extrato">
              <thead>
                <tr>
                  <th scope="col">Data</th>
                  <th scope="col">Histórico</th>
                  <th scope="col">Categoria</th>
                  <th scope="col">Contato</th>
                  <th scope="col" className="text-end">
                    Valor
                  </th>
                  <th scope="col">
                    <span className="visually-hidden">Ações</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map((t) => (
                  <tr key={t.id} className={t.reversedAt ? "text-muted" : undefined}>
                    <td>{formatDateBR(t.date)}</td>
                    <td>
                      {txLabel(t)}
                      {t.reversedAt && <span className="badge text-bg-secondary ms-2">Estornado</span>}
                      {t.titleId && (
                        <span className="small ms-2">
                          (
                          <Link
                            to={
                              t.amountCents >= 0
                                ? "/financas/contas-a-receber/$id"
                                : "/financas/contas-a-pagar/$id"
                            }
                            params={{ id: t.titleId }}
                          >
                            ver conta
                          </Link>
                          )
                        </span>
                      )}
                    </td>
                    <td>{t.categoryName ?? "—"}</td>
                    <td>{t.contactName ?? "—"}</td>
                    <td className={`text-end ${t.amountCents < 0 ? "text-danger" : "text-success"}`}>
                      {formatBRL(t.amountCents)}
                    </td>
                    <td className="text-end">
                      {canWrite && reversible(t) && (
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-danger"
                          aria-label={`Estornar ${txLabel(t)} de ${formatDateBR(t.date)}`}
                          disabled={reverse.isPending}
                          onClick={() => setReversing(t)}
                        >
                          Estornar
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {statement.hasNextPage && (
          <div className="p-3 text-center">
            <button
              type="button"
              className="btn btn-outline-secondary btn-sm"
              disabled={statement.isFetchingNextPage}
              onClick={() => statement.fetchNextPage()}
            >
              Carregar mais
            </button>
          </div>
        )}
      </div>
      {account.data?.kind === "bank" && <ReconcilePanel accountId={id} onChanged={changed} />}
      {entry && <EntryDialog accountId={id} onClose={() => setEntry(false)} onSaved={changed} />}
      <ConfirmDialog
        open={!!reversing}
        title="Estornar lançamento"
        message={
          reversing?.kind === "transfer"
            ? "Estornar a transferência? Os dois lados (origem e destino) recebem o lançamento inverso com a data de hoje."
            : "Estornar o lançamento? Um lançamento inverso com a data de hoje é incluído; o original fica no extrato."
        }
        confirmLabel="Estornar"
        onConfirm={() => {
          if (reversing) reverse.mutate(reversing.id);
          setReversing(null);
        }}
        onCancel={() => setReversing(null)}
      />
    </>
  );
}

function EntryDialog({
  accountId,
  onClose,
  onSaved,
}: {
  accountId: string;
  onClose(): void;
  onSaved(): void;
}) {
  const { companyId, me, api } = useSession();
  const toast = useToast();
  const [open, setOpen] = useState(true);
  const [errors, setErrors] = useState<Record<string, string>>({});
  // Uma chave por formulário aberto: reenviar o mesmo formulário não duplica o lançamento.
  const [key] = useState(() => crypto.randomUUID());
  const [v, setV] = useState({
    direction: "in" as "in" | "out",
    date: todayInSaoPaulo(),
    amountCents: null as number | null,
    description: "",
    financialCategoryId: null as string | null,
  });
  const kind = v.direction === "in" ? "revenue" : "expense";
  const categories = useQuery({
    queryKey: ["fin-categories", companyId, kind],
    enabled: can(me.permissions as Permissions, "financial_categories", "read"),
    queryFn: () => api<{ data: FinancialCategoryView[] }>(`/financial-categories?kind=${kind}`),
  });
  const save = useMutation({
    mutationFn: () =>
      api(`/financial-accounts/${accountId}/transactions`, {
        method: "POST",
        headers: { "Idempotency-Key": key },
        body: JSON.stringify({ ...v, amountCents: v.amountCents ?? 0, description: v.description || null }),
      }),
    onSuccess: () => {
      toast("success", "Lançamento incluído");
      onSaved();
      setOpen(false);
    },
    onError: (e) => {
      if (e instanceof ApiFailure && e.fields) setErrors(e.fields);
      else if (e instanceof ApiFailure && (e.code === "future_date" || e.code === "date_before_opening"))
        setErrors({ date: errorMessage(e) });
      toast("danger", errorMessage(e));
    },
  });
  return (
    <Modal
      open={open}
      title="Novo lançamento"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-outline-secondary" onClick={() => setOpen(false)}>
            Cancelar
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={save.isPending}
            onClick={() => save.mutate()}
          >
            Salvar
          </button>
        </>
      }
    >
      <fieldset className="mb-3">
        <legend className="form-label fs-6">Tipo</legend>
        {(["in", "out"] as const).map((d) => (
          <div key={d} className="form-check form-check-inline">
            <input
              id={`entry-${d}`}
              type="radio"
              name="entry-direction"
              className="form-check-input"
              checked={v.direction === d}
              onChange={() => setV({ ...v, direction: d, financialCategoryId: null })}
            />
            <label className="form-check-label" htmlFor={`entry-${d}`}>
              {d === "in" ? "Entrada" : "Saída"}
            </label>
          </div>
        ))}
      </fieldset>
      <div className="row">
        <Field label="Data" required className="col-md-6" error={errors.date}>
          <input
            type="date"
            value={v.date}
            max={todayInSaoPaulo()}
            onChange={(e) => setV({ ...v, date: e.target.value })}
          />
        </Field>
        <Field label="Valor" required className="col-md-6" error={errors.amountCents}>
          <MoneyInput value={v.amountCents} data-autofocus onChange={(c) => setV({ ...v, amountCents: c })} />
        </Field>
        <Field label="Histórico" className="col-12" error={errors.description}>
          <input
            value={v.description}
            maxLength={200}
            onChange={(e) => setV({ ...v, description: e.target.value })}
          />
        </Field>
        {categories.data && (
          <Field label="Categoria" className="col-12" error={errors.financialCategoryId}>
            <select
              className="form-select"
              value={v.financialCategoryId ?? ""}
              onChange={(e) => setV({ ...v, financialCategoryId: e.target.value || null })}
            >
              <option value="">Sem categoria</option>
              {categories.data.data
                .filter((c) => c.active)
                .map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.path}
                  </option>
                ))}
            </select>
          </Field>
        )}
      </div>
    </Modal>
  );
}
